import { FlightSegment } from 'constants/flight'
import { getBudgetedSelectedDepartingLegPrice, getPendingReturningLegPrice } from 'checkout/selectors/view/flights'
import { applyFeederBudgetToCost } from 'lib/checkout/flights/cart'

export const FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE = 'single'
export const FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED = 'combined'

type BaseOptions = {
  flightItem?: App.Checkout.FlightItem
  /** From `getFeederBundleBudget(state).isFeederBundle` — gates this whole module to the genuine
   * Maldives non-MEL charter-feeder-bundle flow. A flight item's own `bundleBudget` field alone
   * isn't a reliable gate: it can be stale on an older persisted cart after the charter leg is
   * gone, so this must also confirm a real charter item is still in the cart. */
  isFeederBundle: boolean
  fareFamilies: Array<App.FlightFareFamily>
  isReturnType: boolean
  /** Package total baseline (hotel + charter's real current price) each fare family's total is built on. */
  baseline: number
}

type SingleModalOptions = BaseOptions & {
  mode: typeof FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE
  departingJourney?: App.JourneyV2
}

type CombinedModalOptions = BaseOptions & {
  mode: typeof FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED
  currentSegment: FlightSegment
  selectedDepartingFareFamily?: App.FlightFareFamily
  getOverrideTotalCost: (fareFamily: App.FlightFareFamily) => number
  /** Regular (non-member reference) equivalent of `getOverrideTotalCost` for the merged
   * dep+ret override total on a return-type departing tab. Only consulted by
   * `getFareFamilyFeederRegularBudgetLabels`; falls back to `getOverrideTotalCost` itself
   * when omitted (no distinct regular override available). */
  getOverrideRegularTotalCost?: (fareFamily: App.FlightFareFamily) => number
}

type TwoOneWaysCombinedOptions = Omit<CombinedModalOptions, 'mode' | 'isReturnType' | 'getOverrideTotalCost' | 'getOverrideRegularTotalCost'>

type Options = SingleModalOptions | CombinedModalOptions

type CostSelector = (fareFamily?: App.FlightFareFamily) => number

const noReturnFareOverride = (): number => 0

function getFareFamilyMemberTotalCost(fareFamily?: App.FlightFareFamily): number {
  if (!fareFamily) return 0
  return fareFamily.costWithInsurance?.all ?? fareFamily.cost.all
}

/**
 * Non-member reference cost counterpart to `getFareFamilyMemberTotalCost`: the fare family's
 * undiscounted `regularCost`, falling back to its member cost when no distinct regular price
 * exists (no LuxPlus discount to compare against). When the fare carries embedded insurance,
 * the flat insurance premium (`costWithInsurance.all - cost.all`) is added on top of the
 * regular base fare - mirroring how `FareFamilyFareOption`'s own `luxPlusRegularFareCost`
 * builds the non-member price for non-feeder fares.
 */
function getFareFamilyRegularTotalCost(fareFamily?: App.FlightFareFamily): number {
  if (!fareFamily) return 0
  const regularBaseCost = fareFamily.regularCost?.all ?? fareFamily.cost.all
  if (!fareFamily.withEmbeddedInsurance) return regularBaseCost
  const insurancePremium = (fareFamily.costWithInsurance?.all ?? fareFamily.cost.all) - fareFamily.cost.all
  return regularBaseCost + insurancePremium
}

/**
 * Per fare family absolute package totals for the Departing modal: each fare family's own
 * budgeted contribution (reusing `getBudgetedSelectedDepartingLegPrice`, the exact function
 * the real checkout total uses) plus the pending (not yet chosen) returning leg's placeholder.
 * Every option is a self-contained, honest total - not a delta relative to the cheapest tile.
 */
function getDepartingPageLabels(
  flightItem: App.Checkout.FlightItem,
  fareFamilies: Array<App.FlightFareFamily>,
  optionFareFor: CostSelector,
  baseline: number,
): Map<string, number> {
  const pendingReturningLegPrice = getPendingReturningLegPrice(flightItem)
  const map = new Map<string, number>()
  fareFamilies.forEach(fareFamily => {
    const departingContribution = getBudgetedSelectedDepartingLegPrice(flightItem, optionFareFor(fareFamily))
    map.set(fareFamily.id, baseline + departingContribution + pendingReturningLegPrice)
  })
  return map
}

/**
 * Per fare family absolute package totals for the Returning modal: the already-selected
 * departing leg's budgeted contribution (reusing the same function the departing page
 * used for it) plus each fare family's own budgeted returning contribution (`applyFeederBudgetToCost`,
 * the exact function that sets the real cart cost at selection time).
 */
function getReturningPageLabels(
  flightItem: App.Checkout.FlightItem,
  fareFamilies: Array<App.FlightFareFamily>,
  optionFareFor: CostSelector,
  selectedDepartingFare: number,
  baseline: number,
): Map<string, number> {
  const departingContribution = getBudgetedSelectedDepartingLegPrice(flightItem, selectedDepartingFare)
  const map = new Map<string, number>()
  fareFamilies.forEach(fareFamily => {
    const optionFare = optionFareFor(fareFamily)
    const returningContribution = flightItem.bundleBudget
      ? applyFeederBudgetToCost(optionFare, flightItem.bundleBudget, 'returning')
      : optionFare
    map.set(fareFamily.id, baseline + departingContribution + returningContribution)
  })
  return map
}

function getSingleModalFeederBudgetLabels(
  { flightItem, fareFamilies, departingJourney, baseline }: SingleModalOptions & { flightItem: App.Checkout.FlightItem },
  costSelector: CostSelector,
): Map<string, number> {
  if (departingJourney) {
    // Returning page modal: the departing leg is already selected.
    const selectedDepartingFare = departingJourney.price?.all?.totalFare ?? 0
    return getReturningPageLabels(flightItem, fareFamilies, costSelector, selectedDepartingFare, baseline)
  }

  return getDepartingPageLabels(flightItem, fareFamilies, costSelector, baseline)
}

function getCombinedModalFeederBudgetLabels(
  { flightItem, fareFamilies, currentSegment, selectedDepartingFareFamily, isReturnType, baseline }: CombinedModalOptions & { flightItem: App.Checkout.FlightItem },
  costSelector: CostSelector,
  overrideTotalCostFor: (fareFamily: App.FlightFareFamily) => number,
): Map<string, number> {
  const isReturningSegment = currentSegment === FlightSegment.RETURNING
  // Departing tab on return-type bookings uses merged dep+ret override totals;
  // every other case uses the fare family's standalone leg fare.
  const optionFareFor: CostSelector = fareFamily => {
    if (!fareFamily) return 0
    if (!isReturningSegment && isReturnType) return overrideTotalCostFor(fareFamily)
    return costSelector(fareFamily)
  }

  if (isReturningSegment) {
    const selectedDepartingFare = costSelector(selectedDepartingFareFamily)
    return getReturningPageLabels(flightItem, fareFamilies, optionFareFor, selectedDepartingFare, baseline)
  }

  return getDepartingPageLabels(flightItem, fareFamilies, optionFareFor, baseline)
}

function computeFeederBudgetLabels(
  options: Options,
  costSelector: CostSelector,
  overrideTotalCostFor: (fareFamily: App.FlightFareFamily) => number,
): Map<string, number> | undefined {
  if (!options.isFeederBundle || !options.flightItem) return undefined

  if (options.mode === FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE) {
    return getSingleModalFeederBudgetLabels({
      ...options,
      flightItem: options.flightItem,
    }, costSelector)
  }

  return getCombinedModalFeederBudgetLabels({
    ...options,
    flightItem: options.flightItem,
  }, costSelector, overrideTotalCostFor)
}

/**
 * Per fare family absolute package totals (member/charged price) for a feeder-bundle upsell
 * option, built on the exact baseline + budget-snap + charter-inclusion rules the real
 * checkout total uses.
 */
export default function getFareFamilyFeederBudgetLabels(options: Options): Map<string, number> | undefined {
  const overrideTotalCostFor = options.mode === FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED
    ? options.getOverrideTotalCost
    : () => 0
  return computeFeederBudgetLabels(options, getFareFamilyMemberTotalCost, overrideTotalCostFor)
}

/**
 * Non-member reference-price counterpart to `getFareFamilyFeederBudgetLabels`: the exact same
 * baseline, budget-snapping, and charter-inclusion rules, run against each fare family's
 * regular (undiscounted) cost instead of its member cost - so the LuxPlus "Non-member $X"
 * comparison price for a feeder-bundle option can never drift from the real member total the
 * way `bundledHotelPrice + rawFareCost` used to (FLY-5300 follow-up).
 */
export function getFareFamilyFeederRegularBudgetLabels(options: Options): Map<string, number> | undefined {
  const overrideRegularTotalCostFor = options.mode === FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED
    ? options.getOverrideRegularTotalCost ?? options.getOverrideTotalCost
    : () => 0
  return computeFeederBudgetLabels(options, getFareFamilyRegularTotalCost, overrideRegularTotalCostFor)
}

/**
 * Per fare family package totals for a two-one-ways feeder upsell. Charter feeder flights are
 * always two independent one-way legs, so they never need the paired-return override logic
 * required by genuine return fares.
 */
export function getTwoOneWaysFeederBudgetLabels(options: TwoOneWaysCombinedOptions): Map<string, number> | undefined {
  return computeFeederBudgetLabels({
    ...options,
    mode: FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED,
    isReturnType: false,
    getOverrideTotalCost: noReturnFareOverride,
  }, getFareFamilyMemberTotalCost, noReturnFareOverride)
}

/** Non-member reference-price counterpart to {@link getTwoOneWaysFeederBudgetLabels}. */
export function getTwoOneWaysFeederRegularBudgetLabels(options: TwoOneWaysCombinedOptions): Map<string, number> | undefined {
  return computeFeederBudgetLabels({
    ...options,
    mode: FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED,
    isReturnType: false,
    getOverrideTotalCost: noReturnFareOverride,
  }, getFareFamilyRegularTotalCost, noReturnFareOverride)
}
